import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import AdTrackingService from '../../../services/AdTrackingService';

export const getUserId = () => {
  try {
    const authDataStr = localStorage.getItem('authData');
    if (!authDataStr) {
      console.warn('[Storage] No authData found');
      return 'anonymous';
    }
    const authData = JSON.parse(authDataStr);
    return authData?.user?._id || 'anonymous';
  } catch (error) {
    console.error('[Storage] Error reading userId:', error.message);
    return 'anonymous';
  }
};

export const getCanvasStorageKey = (userId, canvasId) => {
  if (!canvasId) {
    console.warn('No canvasId provided for storage key');
    return null;
  }
  return `user_widgets_${userId}_${canvasId}`;
};

// Load widgets for a specific canvas with better error handling
export const loadPersistedWidgets = (userId, canvasId) => {
  try {
    if (!canvasId) {
      console.warn('Cannot load widgets: No canvas ID provided');
      return [];
    }
    
    const key = getCanvasStorageKey(userId, canvasId);
    if (!key) return [];
    
    const savedWidgets = localStorage.getItem(key);
    const parsedWidgets = savedWidgets ? JSON.parse(savedWidgets) : [];
    
    // Ensure consistent widget format and canvas ID
    return parsedWidgets.map(widget => ({
      ...widget,
      id: widget._id || widget.id,
      canvasId: canvasId,
      data: widget.data || {},
      config: widget.config || {}
    }));
  } catch (error) {
    return [];
  }
};

// Save widgets with better validation
export const saveWidgets = (widgets, userId, canvasId) => {
  try {
    if (!canvasId) {
      return;
    }
    
    const key = getCanvasStorageKey(userId, canvasId);
    if (!key) return;
    
    // Filter to ensure we only save widgets that belong to this canvas
    const canvasWidgets = widgets.filter(widget => widget.canvasId === canvasId);
    
    localStorage.setItem(key, JSON.stringify(canvasWidgets));
    localStorage.setItem(`last_sync_${canvasId}`, new Date().getTime().toString());
  } catch (error) {
    console.error('Error saving widgets to storage:', error);
  }
};

// Should we sync with backend?
export const shouldSyncWithBackend = (canvasId) => {
  try {
    const lastSync = localStorage.getItem(`last_sync_${canvasId}`);
    if (!lastSync) return true;
    
    const now = new Date().getTime();
    const syncTime = parseInt(lastSync, 10);
    
    // Sync if more than 30 minutes have passed
    return (now - syncTime) > 30 * 60 * 1000;
  } catch (error) {
    return true;
  }
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const authData = JSON.parse(localStorage.getItem('authData') || '{}');
  return !!authData.accessToken;
};

// Process a user query through the cashbot
export const processUserCashbotQuery = createAsyncThunk(
  'userDashboard/processCashbotQuery',
  async ({ query, canvasId }, { rejectWithValue }) => {
    try {
      if (!canvasId) {
        return rejectWithValue('Canvas ID is required');
      }
      const response = await apiClient.post(`/canvas/${canvasId}/dashboard/cashbot/query`, 
        { query });
      return response.data;
    } catch (error) {
      console.error('CashBot query error:', error);
      return rejectWithValue(
        typeof error === 'string' ? error : 
          error.response?.data?.message || 
          error.message || 
          'Failed to process query'
      );
    }
  }
);

// Create widget from cashbot query
export const createUserWidgetFromCashbot = createAsyncThunk(
  'userDashboard/createWidgetFromCashbot',
  async ({ query, widgetConfig, canvasId }, { rejectWithValue }) => {
    try {
      const authData = JSON.parse(localStorage.getItem('authData') || '{}');
      const userId = authData?.user?._id;
      
      const response = await apiClient.post(
        `/canvas/${canvasId}/dashboard/cashbot/create-widget`,
        {  
          query: query,
          widgetConfig: {
            ...widgetConfig,
            data: widgetConfig.data,
            visualization: widgetConfig?.visualization
          },
        }
      );
      
      // Format response
      const newWidget = {
        ...response.data,
        id: response.data._id || response.data.id || `widget_${Date.now()}`,
        canvasId: canvasId,
        userId: userId
      };
      
      // Save widget to localStorage
      const existingWidgets = loadPersistedWidgets(userId, canvasId);
      saveWidgets([...existingWidgets, newWidget], userId, canvasId);
      
      return newWidget;
    } catch (error) {
      console.error('Error creating user widget:', error);
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to create widget'
      );
    }
  }
);

// fetchUserWidgets with caching
export const fetchUserWidgets = createAsyncThunk(
  'userDashboard/fetchWidgets',
  async ({ canvasId, forceRefresh = false } = {}, { rejectWithValue }) => {
    try { 
      const authData = JSON.parse(localStorage.getItem('authData') || '{}');
      const userId = authData?.user?._id;
      
      // Always fetch from backend if forceRefresh is true
      if (forceRefresh) {
        try {
          const response = await apiClient.get(`/canvas/${canvasId}/dashboard/widgets`);
          
          // Normalize response format
          let widgets = [];
          if (Array.isArray(response.data)) {
            widgets = response.data;
          } else if (response.data?.widgets && Array.isArray(response.data.widgets)) {
            widgets = response.data.widgets;
          } else if (response.data) {
            widgets = [response.data];
          }
          
          // Format widgets consistently
          const formattedWidgets = widgets.map(widget => ({
            id: widget._id || widget.id,
            _id: widget._id || widget.id,
            title: widget.title || 'Untitled Widget',
            type: widget.type || 'metric',
            query: widget.config?.query || widget.query || '',
            data: widget.data || {},
            config: widget.config || {},
            createdAt: widget.createdAt || new Date().toISOString(),
            updatedAt: widget.updatedAt || new Date().toISOString(),
            userId: widget.userId || userId,
            canvasId: canvasId,
            position: widget.position || { x: 0, y: 0, w: 6, h: 4 },
            updateInterval: widget.config?.updateInterval || 3600000,
          }));
          
          // Clear and update local storage
          localStorage.removeItem(getCanvasStorageKey(userId, canvasId));
          saveWidgets(formattedWidgets, userId, canvasId);
          localStorage.setItem(`last_sync_${canvasId}`, new Date().getTime().toString());
          
          return {
            widgets: formattedWidgets,
            canvasId,
            fromCache: false
          };
        } catch (error) {
          throw error;
        }
      }
      
      // Check if we need to fetch from backend
      if (!shouldSyncWithBackend(canvasId)) {
        const cachedWidgets = loadPersistedWidgets(userId, canvasId);
        if (cachedWidgets.length > 0) {
          return {
            widgets: cachedWidgets,
            canvasId,
            fromCache: true
          };
        }
      }
      
      // If we get here, we need to fetch from backend
      const response = await apiClient.get(`/canvas/${canvasId}/dashboard/widgets`);
      
      // Normalize response format
      let widgets = [];
      if (Array.isArray(response.data)) {
        widgets = response.data;
      } else if (response.data?.widgets && Array.isArray(response.data.widgets)) {
        widgets = response.data.widgets;
      } else if (response.data) {
        widgets = [response.data];
      }
      
      // Format widgets consistently
      const formattedWidgets = widgets.map(widget => ({
        id: widget._id || widget.id,
        _id: widget._id || widget.id,
        title: widget.title || 'Untitled Widget',
        type: widget.type || 'metric',
        query: widget.config?.query || widget.query || '',
        data: widget.data || {},
        config: widget.config || {},
        createdAt: widget.createdAt || new Date().toISOString(),
        updatedAt: widget.updatedAt || new Date().toISOString(),
        userId: widget.userId || userId,
        canvasId: canvasId,
        position: widget.position || { x: 0, y: 0, w: 6, h: 4 },
        updateInterval: widget.config?.updateInterval || 3600000,
      }));
      
      // Clear and update local storage
      localStorage.removeItem(getCanvasStorageKey(userId, canvasId));
      saveWidgets(formattedWidgets, userId, canvasId);
      localStorage.setItem(`last_sync_${canvasId}`, new Date().getTime().toString());
      
      return {
        widgets: formattedWidgets,
        canvasId,
        fromCache: false
      };
    } catch (error) {
      console.error('Error fetching widgets:', error);
      // Try to return cached widgets in case of error
      const userId = getUserId();
      const cachedWidgets = loadPersistedWidgets(userId, canvasId);
      
      if (cachedWidgets.length > 0) {
        return {
          widgets: cachedWidgets,
          canvasId,
          fromCache: true,
          error: error.message
        };
      }
      
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch widgets');
    }
  }
);

// Delete a user widget
export const deleteUserWidget = createAsyncThunk(
  'userDashboard/deleteWidget',
  async ({ widgetId, canvasId }, { rejectWithValue }) => {
    try {
      const authData = JSON.parse(localStorage.getItem('authData') || '{}');
      const userId = authData?.user?._id;
            
      try {
        await apiClient.delete(`/canvas/${canvasId}/dashboard/widgets/${widgetId}`);
      } catch (error) {
        if (error.response?.status !== 404) {
          throw error;
        }
      }
      
      // Remove from localStorage
      const persistedWidgets = loadPersistedWidgets(userId, canvasId);
      const updatedWidgets = persistedWidgets.filter(w => 
        w.id !== widgetId && w._id !== widgetId
      );
      saveWidgets(updatedWidgets, userId, canvasId);
      
      return { widgetId, canvasId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to delete widget'
      );
    }
  }
);

//  update user widget title
export const updateUserWidgetTitle = createAsyncThunk(
  'userDashboard/updateUserWidgetTitle',
  async ({ widgetId, canvasId, updateData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/canvas/${canvasId}/dashboard/widgets/${widgetId}`, updateData);

      return { ...response.data, canvasId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Thunk to refresh user widget data
export const refreshUserWidget = createAsyncThunk(
  'userDashboard/refreshWidget',
  async ({ widgetId, canvasId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/canvas/${canvasId}/dashboard/widgets/${widgetId}/refresh`, {});

      return { ...response.data, canvasId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const refreshUserWidgetData = createAsyncThunk(
  'userDashboard/refreshWidgetData',
  async ({ widgetId, canvasId }, { rejectWithValue }) => {
    try {
      if (!widgetId || !canvasId) {
        console.error('[Refresh] Missing widgetId or canvasId:', { widgetId, canvasId });
        return rejectWithValue('Widget ID and Canvas ID are required');
      }
      try {
        const response = await apiClient.post(`/canvas/${canvasId}/dashboard/widgets/${widgetId}/refresh`);
        
        // Update localStorage
        const userId = getUserId();
        const persistedWidgets = loadPersistedWidgets(userId, canvasId);
        const widgetIndex = persistedWidgets.findIndex(w => 
          w.id === widgetId || w._id === widgetId
        );
        
        if (widgetIndex !== -1) {
          persistedWidgets[widgetIndex].data = response.data;
          persistedWidgets[widgetIndex].lastUpdated = new Date().toISOString();
          saveWidgets(persistedWidgets, userId, canvasId);
        }
        
        return { 
          widgetId, 
          data: response.data,
          canvasId
        };
      } catch (error) {
        console.error('[Refresh] API error for widget:', widgetId, error.message);
        
        // If widget not found on server, generate mock data
        if (error.response?.status === 404) {
          console.warn('[Refresh] Widget not found on server:', widgetId);
          return {
            widgetId,
            canvasId,
            data: {
              message: 'Widget data could not be refreshed',
              timestamp: new Date().toISOString()
            }
          };
        }
        throw error;
      }
    } catch (error) {
      console.error('[Refresh] Thunk error:', error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to refresh widget data');
    }
  }
);

// Sync widgets with backend - new function matching admin pattern
export const syncUserWidgetsWithBackend = createAsyncThunk(
  'userDashboard/syncWidgetsWithBackend',
  async ({ canvasId }, { getState, rejectWithValue }) => {
    try {
      if (!isAuthenticated()) {
        return { synced: false, reason: 'Not authenticated' };
      }
      
      if (!canvasId) {
        return { synced: false, reason: 'No canvas ID provided' };
      }
      
      const userId = getUserId();
      const state = getState();
      const localWidgets = state.userDashboard.widgets.filter(w => w.canvasId === canvasId);
      
      try {
        // Sync local widgets to backend
        const response = await apiClient.post(`/canvas/${canvasId}/dashboard/widgets/sync`, 
          { widgets: localWidgets, userId }
        );
        
        // Update localStorage
        saveWidgets(response.data, userId, canvasId);
        localStorage.setItem(`last_sync_${canvasId}`, new Date().getTime().toString());
        
        return { 
          synced: true, 
          widgets: response.data,
          canvasId 
        };
      } catch (error) {
        if (error.response?.status === 404) {
          return { synced: false, reason: 'Sync endpoint not available' };
        }
        throw error;
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to sync widgets');
    }
  }
);

// Update handleCanvasChange
export const handleCanvasChange = createAsyncThunk(
  'userDashboard/handleCanvasChange',
  async ({ canvasId, clearWidgets = true }, { dispatch }) => {
    return { canvasId, clearWidgets };
  }
);

//check ad status
export const checkAdStatus = createAsyncThunk(
  'userDashboard/checkAdStatus',
  async (canvasId, { rejectWithValue, getState }) => {
    try {
      const authData = JSON.parse(localStorage.getItem('authData') || '{}');
      const userId = authData?.user?._id;
      const state = getState();
      const selectedCanvasId = canvasId || state.canvas?.selectedCanvas?.id;
            
      // Try to get cached status first
      const cachedStatus = AdTrackingService.getLastAdStatus(userId, selectedCanvasId);
      const now = new Date();
      
      // If we have cached status that's less than 30 seconds old, use it
      if (cachedStatus && cachedStatus.lastChecked) {
        const lastChecked = new Date(cachedStatus.lastChecked);
        const diffSeconds = (now - lastChecked) / 1000;
        
        if (diffSeconds < 30) {
          return cachedStatus;
        }
      }
      
      const response = await apiClient.get(
        `/canvas/${selectedCanvasId}/dashboard/cashbot/ad-status`);
      
      const status = response.data;
      
      AdTrackingService.saveAdStatus(userId, selectedCanvasId, status);
      
      return status;
    } catch (error) {
      console.error('[AdStatus] Error checking ad status:', error.message);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to check ad status');
    }
  }
);

// thunk for recording ad views
export const recordAdView = createAsyncThunk(
  'userDashboard/recordAdView',
  async ({ adId, status, durationWatched, selectedCanvasId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const authData = JSON.parse(localStorage.getItem('authData') || '{}');
      const userId = authData?.user?._id;
      const canvasId = selectedCanvasId || JSON.parse(localStorage.getItem('selectedCanvas'))?.id;

      if (!canvasId || !userId) {
        return rejectWithValue('Authentication or canvas selection required');
      }
                  
      const response = await apiClient.post(
        `/canvas/${canvasId}/dashboard/cashbot/ad-view`,
        {
          adId,
          status,
          durationWatched
        }
      );
      
      
      AdTrackingService.incrementAdView(userId, canvasId);
      
      setTimeout(() => {
        dispatch(checkAdStatus(canvasId)).catch(err => {
          console.error('[RecordAd] Failed to check status:', err);
        });
      }, 300);
      
      return response.data;
    } catch (error) {
      console.error('[RecordAd] Error recording ad view:', error.message);
      
      try {
        dispatch(checkAdStatus()).catch(statusError => {
          console.error('[RecordAd] Failed to check status after error:', statusError);
        });
      } catch (statusError) {
        console.error('[RecordAd] Exception checking status:', statusError);
      }
      
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to record ad view');
    }
  }
);

//Fetch Insights
export const fetchUserInsights = createAsyncThunk(
  'userDashboard/fetchUserInsights',
  async (canvasId, { rejectWithValue }) => {
    try {
      if ( !canvasId) {
        return rejectWithValue('Authentication or canvas selection required');
      }
      const response = await apiClient.get(`/canvas/${canvasId}/dashboard/insights`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch insights');
    }
  }
);

//  save user widget layout
export const saveUserWidgetLayout = createAsyncThunk(
  'userDashboard/saveUserWidgetLayout',
  async ({ layoutMap, canvasId }, { rejectWithValue }) => {
    try {
     
      const response = await apiClient.patch(`canvas/${canvasId}/dashboard/layout/save`, {
        layoutMap
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to save widget layout'
      );
    }
  }
);

// update multiple user widget positions
export const updateMultipleUserWidgetPositions = createAsyncThunk(
  'userDashboard/updateMultipleUserWidgetPositions',
  async ({ layoutMap, canvasId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`canvas/${canvasId}/dashboard/widgets/positions/bulk`, {
        layoutMap
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update widget positions'
      );
    }
  }
);

// Fetch all active tutorials for users
export const fetchUserTutorials = createAsyncThunk(
  'userDashboard/fetchTutorials',
  async (
    { 
      page = 1, 
      limit = 100, 
      searchTerm = '', 
      category = '', 
      level = '', 
      sortBy = 'sequence', 
      sortOrder = 'asc',
      canvasId 
    } = {},
    { rejectWithValue }
  ) => {
    try {
      if (!canvasId) {
        return rejectWithValue('Canvas ID is required');
      }

      const params = { 
        page, 
        limit, 
        searchTerm, 
        category, 
        level, 
        sortBy, 
        sortOrder,
        isActive: true
      };
      

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await apiClient.get(`/canvas/${canvasId}/dashboard/tutorials`, { params });

      return {
        tutorials: response.data.data.tutorials,
        pagination: response.data.data.pagination
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Fetch single tutorial
export const fetchUserTutorialById = createAsyncThunk(
  'userDashboard/fetchTutorialById',
  async ({ id, canvasId }, { rejectWithValue }) => {
    try {
      if (!canvasId) {
        return rejectWithValue('Canvas ID is required');
      }

      const response = await apiClient.get(`/canvas/${canvasId}/dashboard/tutorials/${id}`);
      
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  loading: false,
  analyticsLoading: false,
  cashbotLoading: false,
  widgets: [],
  cashbot: {
    isOpen: false,
    messages: [],
    isTyping: false,
    lastQueryResult: null,
    disclaimerAccepted: false 
  },
  error: null,
  lastSyncTime: null,
  adStatus: {
    loading: false,
    hasAccess: false,
    adsRequired: 2,
    adsWatched: 0,
    expiresAt: null,
    remainingMinutes: 0,
    isPremium: false,
    error: null
  },
  adViewInProgress: false,
  insights: null,
  insightsLoading: false,
  insightsError: null,
  tutorials: {
    tutorials: [],
    pagination: {
      limit: 100,
      page: 1,
      pages: 1,
      total: 0
    },
    loading: false,
    error: null,
    selectedTutorial: null,
    filters: {
      category: '',
      level: '',
      searchTerm: ''
    }
  }
};

// Create the slice with updated patterns
const userDashboardSlice = createSlice({
  name: 'userDashboard',
  initialState,
  reducers: {
    toggleUserCashbot: (state) => {
      state.cashbot.isOpen = !state.cashbot.isOpen;
    },
    closeUserCashbot: (state) => {
      state.cashbot.isOpen = false;
    },
    addUserCashbotMessage: (state, action) => {
      state.cashbot.messages.push(action.payload);
    },
    setUserCashbotTyping: (state, action) => {
      state.cashbot.isTyping = action.payload;
    },
    clearUserCashbotMessages: (state) => {
      state.cashbot.messages = [];
    },
    loadUserCashbotHistory: (state, action) => {
      state.cashbot.messages = action.payload;
    },
    acceptCashbotDisclaimer: (state) => {  
      state.cashbot.disclaimerAccepted = true;
    },
    // Add these to match admin pattern
    addUserWidget: (state, action) => {
      const newWidget = action.payload;
      const userId = getUserId();
      
      state.widgets.push(newWidget);
      
      if (newWidget.canvasId) {
        // Update localStorage for this canvas
        const canvasWidgets = state.widgets.filter(w => w.canvasId === newWidget.canvasId);
        saveWidgets(canvasWidgets, userId, newWidget.canvasId);
      }
    },
    removeUserWidget: (state, action) => {
      const { widgetId, canvasId } = action.payload;
      const userId = getUserId();
      
      state.widgets = state.widgets.filter(widget => 
        !(widget.id === widgetId || widget._id === widgetId)
      );
      
      if (canvasId) {
        // Update localStorage for this canvas
        const canvasWidgets = state.widgets.filter(w => w.canvasId === canvasId);
        saveWidgets(canvasWidgets, userId, canvasId);
      }
    },
    updateUserWidget: (state, action) => {
      const { widgetId, canvasId, ...updates } = action.payload;
      const userId = getUserId();
      
      const index = state.widgets.findIndex(widget => 
        (widget.id === widgetId || widget._id === widgetId) && 
        widget.canvasId === canvasId
      );
      
      if (index !== -1) {
        state.widgets[index] = { 
          ...state.widgets[index], 
          ...updates,
          lastUpdated: new Date().toISOString()
        };
        
        if (canvasId) {
          // Update localStorage for this canvas
          const canvasWidgets = state.widgets.filter(w => w.canvasId === canvasId);
          saveWidgets(canvasWidgets, userId, canvasId);
        }
      }
    },
    clearAllUserWidgets: (state, action) => {
      const canvasId = action.payload;
      const userId = getUserId();
      
      if (canvasId) {
        // Only clear widgets for this canvas
        state.widgets = state.widgets.filter(w => w.canvasId !== canvasId);
        
        // Clear localStorage for this canvas
        localStorage.removeItem(getCanvasStorageKey(userId, canvasId));
      } else {
        // Clear all widgets
        state.widgets = [];
      }
    },
    setInitialWidgets: (state, action) => {
      const { widgets, canvasId } = action.payload;
      
      if (canvasId) {
        // Remove existing widgets for this canvas
        state.widgets = state.widgets.filter(w => w.canvasId !== canvasId);
        
        // Add new widgets for this canvas
        state.widgets.push(...widgets);
      } else {
        state.widgets = widgets;
      }
      
      state.lastSyncTime = new Date().toISOString();
    },
    setTutorialCategory: (state, action) => {
      state.tutorials.filters.category = action.payload;
    },
    setTutorialLevel: (state, action) => {
      state.tutorials.filters.level = action.payload;
    },
    setTutorialSearchTerm: (state, action) => {
      state.tutorials.filters.searchTerm = action.payload;
    },
    clearTutorialFilters: (state) => {
      state.tutorials.filters = {
        category: '',
        level: '',
        searchTerm: ''
      };
    },
    setSelectedTutorial: (state, action) => {
      state.tutorials.selectedTutorial = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Process cashbot query
      .addCase(processUserCashbotQuery.pending, (state) => {
        state.cashbotLoading = true;
        state.cashbot.isTyping = true;
      })
      .addCase(processUserCashbotQuery.fulfilled, (state, action) => {
        state.cashbotLoading = false;
        state.cashbot.isTyping = false;
        
        // Add the bot response to the chat history
        if (action.payload && action.payload.answer) {
          const botMessage = {
            id: Date.now(),
            type: 'bot',
            content: action.payload.answer,
            timestamp: new Date().toISOString(),
            data: action.payload.data,
            canCreateWidget: action.payload.canCreateWidget,
            widgetType: action.payload.widgetType,
            widgetConfig: action.payload.widgetConfig,
            query: action.meta?.arg?.query || '',
          };
          
          state.cashbot.messages.push(botMessage);
          state.cashbot.lastQueryResult = action.payload;
        }
      })
      .addCase(processUserCashbotQuery.rejected, (state, action) => {
        state.cashbotLoading = false;
        state.cashbot.isTyping = false;
        
        // Make sure we only store serializable values
        state.error = typeof action.payload === 'string' 
          ? action.payload 
          : action.error?.message || 'An error occurred';
          
        // Add error message to chat
        const errorMessage = {
          id: Date.now(),
          type: 'bot',
          content: `Sorry, I encountered an error: ${
            typeof action.payload === 'string' 
              ? action.payload 
              : 'Failed to process your query. Please try again.'
          }`,
          timestamp: new Date().toISOString(),
          isError: true,
        };
        
        state.cashbot.messages.push(errorMessage);
      })
      
      // Create widget from cashbot
      .addCase(createUserWidgetFromCashbot.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUserWidgetFromCashbot.fulfilled, (state, action) => {
        state.loading = false;
        
        // Format new widget
        const newWidget = {
          id: action.payload._id || action.payload.id || `widget_${Date.now()}`,
          _id: action.payload._id || action.payload.id || `widget_${Date.now()}`,
          title: action.payload.title || 'Untitled Widget',
          type: action.payload.type || 'metric',
          query: action.payload.query || '',
          data: action.payload.data || {},
          config: action.payload.config || {},
          createdAt: action.payload.createdAt || new Date().toISOString(),
          updatedAt: action.payload.updatedAt || new Date().toISOString(),
          userId: action.payload.userId,
          canvasId: action.payload.canvasId || action.meta.arg.canvasId,
          position: action.payload.position || { x: 0, y: 0, w: 6, h: 4 },
          updateInterval: action.payload.updateInterval || 3600000,
        };
        
        // Remove any existing widget with the same ID
        state.widgets = state.widgets.filter(w => 
          !((w.id === newWidget.id || w._id === newWidget._id) && 
            w.canvasId === newWidget.canvasId)
        );
        
        // Add the new widget
        state.widgets.push(newWidget);
        
        // Add success message to chat
        const successMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: `Widget "${newWidget.title}" has been created successfully and added to your dashboard!`,
          timestamp: new Date().toISOString(),
          isSuccess: true,
        };
        state.cashbot.messages.push(successMessage);
      })
      .addCase(createUserWidgetFromCashbot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      // Fetch user widgets
      .addCase(fetchUserWidgets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserWidgets.fulfilled, (state, action) => {
        state.loading = false;
        const { widgets, canvasId, fromCache } = action.payload;
        
        if (!canvasId) {
          return;
        }
        
        // Remove widgets for this canvas
        state.widgets = state.widgets.filter(w => w.canvasId !== canvasId);
        
        // Add new widgets for this canvas
        state.widgets.push(...widgets);
        
        // Update last sync time if not from cache
        if (!fromCache) {
          state.lastSyncTime = new Date().toISOString();
        }
      })
      .addCase(fetchUserWidgets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      // Delete widget
      .addCase(deleteUserWidget.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserWidget.fulfilled, (state, action) => {
        state.loading = false;
        
        const { widgetId, canvasId } = action.payload;
        
        // Remove widget from state
        state.widgets = state.widgets.filter(widget => 
          !((widget.id === widgetId || widget._id === widgetId) && 
            widget.canvasId === canvasId)
        );
      })
      .addCase(deleteUserWidget.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      // Refresh widget data
      .addCase(refreshUserWidgetData.pending, (state) => {
        // No need to set loading flag for individual widget refresh
      })
      .addCase(refreshUserWidgetData.fulfilled, (state, action) => {
        const { widgetId, data, canvasId } = action.payload;
        
        // Find widget by ID
        const widgetIndex = state.widgets.findIndex(widget => {
          const matches = (widget.id === widgetId || widget._id === widgetId) && 
                         widget.canvasId === canvasId;
          return matches;
        });
        
        if (widgetIndex !== -1) {
          // Update widget data
          state.widgets[widgetIndex].data = data;
          state.widgets[widgetIndex].lastUpdated = new Date().toISOString();
        }
      })
      .addCase(refreshUserWidgetData.rejected, (state, action) => {
        // Don't set global error for individual widget refresh failure
        console.error('Widget refresh failed:', action.payload || action.error.message);
      })
      
      // Sync widgets with backend
      .addCase(syncUserWidgetsWithBackend.pending, (state) => {
        state.loading = true;
      })
      .addCase(syncUserWidgetsWithBackend.fulfilled, (state, action) => {
        state.loading = false;
        
        if (action.payload.synced) {
          const { widgets, canvasId } = action.payload;
          
          // Remove widgets for this canvas
          state.widgets = state.widgets.filter(w => w.canvasId !== canvasId);
          
          // Add updated widgets for this canvas
          state.widgets.push(...widgets);
        }
      })
      .addCase(syncUserWidgetsWithBackend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      // Handle canvas change
      .addCase(handleCanvasChange.fulfilled, (state, action) => {
        const { canvasId, clearWidgets } = action.payload;
        const userId = getUserId();
        
        if (clearWidgets) {
          // Clear all widgets for this canvas
          state.widgets = state.widgets.filter(w => w.canvasId !== canvasId);
          
          // Clear localStorage for this canvas
          localStorage.removeItem(getCanvasStorageKey(userId, canvasId));
        } else {
          // Just switch the active canvas, keep widgets
          state.activeCanvasId = canvasId;
        }
      })
      
      // Ad status cases
      .addCase(checkAdStatus.pending, (state) => {
        state.adStatus.loading = true;
        state.adStatus.error = null;
      })
      .addCase(checkAdStatus.fulfilled, (state, action) => {
        state.adStatus.loading = false;
        
        if (action.payload) {
          state.adStatus = {
            ...state.adStatus,
            ...action.payload,
            loading: false,
            error: null
          };
        }
      })
      .addCase(checkAdStatus.rejected, (state, action) => {
        state.adStatus.loading = false;
        state.adStatus.error = action.payload || action.error.message;
      })
      
      // Ad view recording cases
      .addCase(recordAdView.pending, (state) => {
        state.adViewInProgress = true;
      })
      .addCase(recordAdView.fulfilled, (state) => {
        state.adViewInProgress = false;
      })
      .addCase(recordAdView.rejected, (state) => {
        state.adViewInProgress = false;
      })
        // Fetch user insights
      .addCase(fetchUserInsights.pending, (state) => {
        state.insightsLoading = true;
        state.insightsError = null;
      })
      .addCase(fetchUserInsights.fulfilled, (state, action) => {
        state.insightsLoading = false;
        state.insights = action.payload;
      })
      .addCase(fetchUserInsights.rejected, (state, action) => {
        state.insightsLoading = false;
        state.insightsError = action.payload || action.error.message;
      })
       // Update user widget
    .addCase(updateUserWidgetTitle.fulfilled, (state, action) => {
      if (state.canvases) {
        const canvasIndex = state.canvases.findIndex(c => c.id === action.payload.canvasId);
        if (canvasIndex !== -1 && state.canvases[canvasIndex].widgets) {
          const widgetIndex = state.canvases[canvasIndex].widgets.findIndex(
            w => w.id === action.payload.id || w._id === action.payload._id
          );
          if (widgetIndex !== -1) {
            state.canvases[canvasIndex].widgets[widgetIndex] = {
              ...state.canvases[canvasIndex].widgets[widgetIndex],
              ...action.payload
            };
          }
        }
      }
      state.loading = false;
    })
    .addCase(updateUserWidgetTitle.pending, (state) => {
      state.loading = true;
    })
    .addCase(updateUserWidgetTitle.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to update widget';
    })

    // Refresh user widget
    .addCase(refreshUserWidget.fulfilled, (state, action) => {
      if (state.canvases) {
        const canvasIndex = state.canvases.findIndex(c => c.id === action.payload.canvasId);
        if (canvasIndex !== -1 && state.canvases[canvasIndex].widgets) {
          const widgetIndex = state.canvases[canvasIndex].widgets.findIndex(
            w => w.id === action.payload.id || w._id === action.payload._id
          );
          if (widgetIndex !== -1) {
            state.canvases[canvasIndex].widgets[widgetIndex] = action.payload;
          }
        }
      }
      state.loading = false;
    })
    .addCase(refreshUserWidget.pending, (state) => {
      state.loading = true;
    })
    .addCase(refreshUserWidget.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to refresh widget';
    })
    .addCase(saveUserWidgetLayout.pending, (state) => {
      state.layoutSaving = true;
      state.layoutError = null;
    })
    .addCase(saveUserWidgetLayout.fulfilled, (state, action) => {
      state.layoutSaving = false;
      state.lastLayoutSave = new Date().toISOString();
    })
    .addCase(saveUserWidgetLayout.rejected, (state, action) => {
      state.layoutSaving = false;
      state.layoutError = action.payload;
    })
    .addCase(updateMultipleUserWidgetPositions.pending, (state) => {
      state.layoutSaving = true;
    })
    .addCase(updateMultipleUserWidgetPositions.fulfilled, (state, action) => {
      state.layoutSaving = false;
      state.lastLayoutSave = new Date().toISOString();
      // Update widget positions in state if needed
      if (action.payload?.widgets) {
        action.payload.widgets.forEach(updatedWidget => {
          const index = state.widgets.findIndex(w => w._id === updatedWidget._id);
          if (index !== -1) {
            state.widgets[index] = { ...state.widgets[index], ...updatedWidget };
          }
        });
      }
    })
    .addCase(updateMultipleUserWidgetPositions.rejected, (state, action) => {
      state.layoutSaving = false;
      state.layoutError = action.payload;
    })
      
      // Fetch user tutorials
      .addCase(fetchUserTutorials.pending, (state) => {
        state.tutorials.loading = true;
        state.tutorials.error = null;
      })
      .addCase(fetchUserTutorials.fulfilled, (state, action) => {
        state.tutorials.loading = false;
        state.tutorials.tutorials = action.payload.tutorials;
        state.tutorials.pagination = action.payload.pagination;
      })
      .addCase(fetchUserTutorials.rejected, (state, action) => {
        state.tutorials.loading = false;
        state.tutorials.error = action.payload || action.error.message;
      })
      
      // Fetch single tutorial
      .addCase(fetchUserTutorialById.pending, (state) => {
        state.tutorials.loading = true;
        state.tutorials.error = null;
      })
      .addCase(fetchUserTutorialById.fulfilled, (state, action) => {
        state.tutorials.loading = false;
        state.tutorials.selectedTutorial = action.payload;
      })
      .addCase(fetchUserTutorialById.rejected, (state, action) => {
        state.tutorials.loading = false;
        state.tutorials.error = action.payload || action.error.message;
      })
  }
});

export const { 
  toggleUserCashbot, 
  closeUserCashbot, 
  addUserCashbotMessage, 
  setUserCashbotTyping, 
  clearUserCashbotMessages, 
  loadUserCashbotHistory,
  acceptCashbotDisclaimer,
  addUserWidget,
  removeUserWidget,
  updateUserWidget,
  clearAllUserWidgets,
  setInitialWidgets,
  setTutorialCategory,
  setTutorialLevel,
  setTutorialSearchTerm,
  clearTutorialFilters,
  setSelectedTutorial
} = userDashboardSlice.actions;

export default userDashboardSlice.reducer;

// Selectors
export const selectUserWidgets = (state) => state.userDashboard?.widgets || [];
export const selectUserCashbot = (state) => state.userDashboard?.cashbot || {
  isOpen: false,
  messages: [],
  isTyping: false,
  lastQueryResult: null
};
export const selectUserDashboardLoading = (state) => state.userDashboard?.loading || false;
export const selectUserCashbotLoading = (state) => state.userDashboard?.cashbotLoading || false;
export const selectUserDashboardError = (state) => state.userDashboard?.error || null;
export const selectUserDashboardLastSyncTime = (state) => state.userDashboard?.lastSyncTime || null;
export const selectAdStatus = (state) => state.userDashboard?.adStatus;
export const selectAdViewInProgress = (state) => state.userDashboard?.adViewInProgress;
export const selectUserTutorials = (state) => state.userDashboard?.tutorials?.tutorials || [];
export const selectTutorialsPagination = (state) => state.userDashboard?.tutorials?.pagination || {
  limit: 100,
  page: 1,
  pages: 1,
  total: 0
};
export const selectTutorialsLoading = (state) => state.userDashboard?.tutorials?.loading || false;
export const selectTutorialsError = (state) => state.userDashboard?.tutorials?.error || null;
export const selectTutorialFilters = (state) => state.userDashboard?.tutorials?.filters || {
  category: '',
  level: '',
  searchTerm: ''
};
export const selectSelectedTutorial = (state) => state.userDashboard?.tutorials?.selectedTutorial || null;
export const selectCashbotDisclaimerAccepted = (state) => 
  state.userDashboard?.cashbot?.disclaimerAccepted || false;